import React from 'react'
import { TemplateTypes, TemplateType } from '../../../models/Template'
import { IncidentV2DetailsStepper } from './IncidentV2DetailsStepper'
import { IncidentV2DefaultField } from './IncidentV2DefaultField'
import {
  IIncidentTypePermission,
  IncidentStatus
} from '../../../models/Incident'
import { IPeoplePickerProps } from '../../../components/person/PeopleTab'
import {
  getSelectedCategoryObject,
  modifyStatusSelection,
  shouldDisableCategoryField
} from '../../../utils/incident/incidentv2'
import { Box } from '@material-ui/core'
import {
  withOneplaceLibraryContext,
  IOneplaceLibraryContextProp
} from '../../OneplaceLibraryProvider'

interface IIncidentV2DetailsProps extends IOneplaceLibraryContextProp {
  onCategoryChange: (value: string | number) => void
  onFetchCategories: (siteId: string, incidentTypeId: string) => Promise<void>
  onStatusChange: (value: string | number) => void
  groupIndex: number
  templateType: TemplateType
  incidentVersion: string
  incidentSiteId?: string
  incidentTypeId?: string
  categories: any[]
  selectedCategory?: string | number
  selectedCategoryName?: string
  selectedStatus?: string | number
  baseStatus?: 'draft' | keyof IncidentStatus | number
  peopleComponent?: React.ComponentType<IPeoplePickerProps>
  incidentTypePermissions?: IIncidentTypePermission
  showPeopleComponent?: boolean
  existingIncident: boolean
}

const IncidentV2DetailsComponent = ({
  ctx,
  onCategoryChange,
  onFetchCategories,
  onStatusChange,
  groupIndex,
  templateType = TemplateTypes.CHECKLIST,
  incidentVersion,
  incidentSiteId,
  incidentTypeId,
  categories,
  selectedCategory,
  selectedCategoryName,
  selectedStatus,
  baseStatus,
  peopleComponent,
  incidentTypePermissions,
  showPeopleComponent,
  existingIncident
}: IIncidentV2DetailsProps) => {
  if (
    groupIndex !== 0 ||
    templateType !== TemplateTypes.INCIDENT ||
    incidentVersion !== 'v2'
  ) {
    return null
  }

  const numericSelectedCategory = Number(selectedCategory)

  const categoriesDisabled = shouldDisableCategoryField(
    existingIncident,
    numericSelectedCategory,
    categories,
    incidentTypePermissions ?? ({} as IIncidentTypePermission)
  )

  const selectedCategoryObject = getSelectedCategoryObject(
    numericSelectedCategory,
    categories
  )

  // display-only fallback: if the previously-selected category has been
  // stripped from `categories` (e.g. the category was deleted server-side),
  // inject a synthetic entry so the Select shows its name instead of
  // rendering blank. It carries disabled: true so it can't be picked even
  // when the select itself is enabled (categoriesDisabled is false). Must
  // never feed into the categoriesDisabled/selectedCategoryObject logic above.
  const isSelectedCategoryMissing =
    existingIncident &&
    numericSelectedCategory > 0 &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    !categories.some((category) => category.id === numericSelectedCategory)

  const categoryDisplayOptions =
    isSelectedCategoryMissing && selectedCategoryName
      ? [
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          ...categories,
          {
            id: numericSelectedCategory,
            label: selectedCategoryName,
            disabled: true
          }
        ]
      : categories

  return (
    <Box style={{ backgroundColor: 'white' }}>
      <IncidentV2DetailsStepper
        status={Number(baseStatus) || 100}
        translate={ctx.i18next.t}
      />
      <IncidentV2DefaultField
        label={ctx.i18next.t('customLabel_incidentCategory')}
        getData={onFetchCategories}
        siteId={incidentSiteId as string}
        incidentTypeId={incidentTypeId}
        selections={categoryDisplayOptions}
        selectedValue={selectedCategory}
        onChange={onCategoryChange}
        disabled={categoriesDisabled}
        required={!categoriesDisabled}
      />
      <IncidentV2DefaultField
        label='Status'
        selections={modifyStatusSelection(
          numericSelectedCategory,
          categories,
          ctx.i18next.t,
          selectedStatus
        )}
        selectedValue={selectedStatus}
        onChange={onStatusChange}
        disabled={
          !(!!selectedCategory &&
            (selectedCategoryObject?.automaticResolve
          || incidentTypePermissions?.accessIncidentCategory))
        }
      />
      {showPeopleComponent && peopleComponent}
    </Box>
  )
}

export const IncidentV2Details = withOneplaceLibraryContext(
  IncidentV2DetailsComponent
)
